import { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import SecretReveal from '../../../components/SecretReveal'
import Pagination from '../../../components/Pagination'

function timeAgo(iso) {
  if (!iso) return null
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins  <  1) return 'just now'
  if (mins  < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days  <  7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year:   'numeric',
    month:  'short',
    day:    'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  })
}

export default function Index({
  api_keys             = [],
  available_workspaces = [],
  reveal               = null,
  errors               = {},
  pagination           = {},
}) {
  const [label, setLabel]               = useState('')
  const [workspaceId, setWorkspaceId]   = useState(available_workspaces[0]?.id ?? '')
  const [clientError, setClientError]   = useState('')
  const [pendingRevoke, setPendingRevoke] = useState(null)

  function validateLabel(value) {
    if (!value.trim()) return 'Label is required.'
    if (value.length > 50) return 'Maximum 50 characters.'
    return ''
  }

  function handleLabelChange(e) {
    setLabel(e.target.value)
    if (clientError) setClientError(validateLabel(e.target.value))
  }

  function handleCreate(e) {
    e.preventDefault()
    const err = validateLabel(label)
    if (err) {
      setClientError(err)
      return
    }
    router.post('/settings/api_keys', { label, workspace_id: workspaceId }, {
      onSuccess: () => setLabel(''),
    })
  }

  function handleRevokeClick(key) {
    setPendingRevoke(key)
  }

  function handleConfirmRevoke() {
    if (!pendingRevoke) return
    router.delete(`/settings/api_keys/${pendingRevoke.id}`, {
      onSuccess: () => setPendingRevoke(null),
      onError:   () => setPendingRevoke(null),
    })
  }

  function handleCancelRevoke() {
    setPendingRevoke(null)
  }

  const labelError = clientError || errors?.label?.[0]

  return (
    <Layout active="api_keys">
      <div className="max-w-4xl space-y-6">
        <h1 className="text-lg font-mono font-bold text-zinc-100">API Keys</h1>
        <p className="text-sm font-mono text-zinc-500">
          Keys across all workspaces where you are an owner.
        </p>

        {/* Secret reveal — shown once after creation */}
        <SecretReveal reveal={reveal} />

        {/* Create form */}
        <section className="border border-zinc-700 rounded-lg p-4 space-y-3">
          <h2 className="text-sm font-mono font-semibold text-zinc-300">New API Key</h2>
          <form onSubmit={handleCreate} className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] space-y-1">
              <input
                type="text"
                value={label}
                onChange={handleLabelChange}
                maxLength={50}
                placeholder="Key name (max 50 chars)"
                className={[
                  'w-full px-3 py-1.5 rounded bg-zinc-800 text-zinc-100 font-mono text-sm',
                  'border transition-colors placeholder:text-zinc-600',
                  'focus:outline-none focus:ring-2 focus:ring-emerald-500/40',
                  labelError
                    ? 'border-red-600 focus:ring-red-500'
                    : 'border-zinc-700 hover:border-zinc-600',
                ].join(' ')}
              />
              {labelError && (
                <p className="text-xs text-red-400 font-mono">{labelError}</p>
              )}
            </div>

            {available_workspaces.length > 1 && (
              <div className="min-w-[180px]">
                <select
                  name="workspace_id"
                  value={workspaceId}
                  onChange={e => setWorkspaceId(e.target.value)}
                  className="w-full px-3 py-1.5 rounded bg-zinc-800 text-zinc-100 font-mono text-sm border border-zinc-700 hover:border-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  {available_workspaces.map(ws => (
                    <option key={ws.id} value={ws.id}>{ws.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="submit"
              className="px-3 py-1.5 rounded-lg bg-emerald-800/60 hover:bg-emerald-700/80 text-emerald-200 border border-emerald-700/40 text-sm font-mono transition-colors shrink-0"
            >
              Create
            </button>
          </form>
        </section>

        {/* API keys table */}
        {api_keys.length === 0 ? (
          <p className="text-sm font-mono text-zinc-600 py-4">
            No active API keys across your owned workspaces.
          </p>
        ) : (
          <div className="border border-zinc-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="bg-zinc-800/60 text-zinc-400 text-left">
                  <th className="px-4 py-2 font-medium">Label</th>
                  <th className="px-4 py-2 font-medium">Workspace</th>
                  <th className="px-4 py-2 font-medium">Prefix</th>
                  <th className="px-4 py-2 font-medium">Created by</th>
                  <th className="px-4 py-2 font-medium">Last used</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-700">
                {api_keys.map(key => (
                  <tr key={key.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-2 text-zinc-100">{key.label}</td>
                    <td className="px-4 py-2">
                      <span className="px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs">
                        {key.workspace_name}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {key.key_prefix
                        ? <span className="text-zinc-600 font-mono text-xs">{key.key_prefix}...</span>
                        : <span className="text-zinc-700 text-xs">—</span>
                      }
                    </td>
                    <td className="px-4 py-2 text-zinc-400 text-xs">
                      {key.created_by_email || <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-4 py-2">
                      {key.last_used_at
                        ? (
                          <span
                            className="text-zinc-400"
                            title={formatDate(key.last_used_at)}
                          >
                            {timeAgo(key.last_used_at)}
                          </span>
                        )
                        : <span className="text-zinc-700">Never used</span>
                      }
                    </td>
                    <td className="px-4 py-2 text-zinc-400">{formatDate(key.created_at)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleRevokeClick(key)}
                        className="px-2 py-1 rounded text-xs text-red-400 border border-red-900 hover:bg-red-950/60 hover:text-red-300 transition-colors"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          current_page={pagination.current_page}
          total_pages={pagination.total_pages}
          onPageChange={page => router.get('/settings/api_keys', { page }, { preserveState: true })}
        />

        {pendingRevoke && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-sm w-full space-y-4 shadow-xl">
              <h2 className="text-sm font-mono font-semibold text-zinc-100">Revoke API Key</h2>
              <p className="text-sm font-mono text-zinc-400">
                Revoke <span className="text-zinc-200 font-semibold">{pendingRevoke.label}</span>
                {' '}from workspace <span className="text-zinc-200 font-semibold">{pendingRevoke.workspace_name}</span>?
                This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelRevoke}
                  className="px-3 py-1.5 rounded text-sm font-mono text-zinc-400 border border-zinc-700 hover:bg-zinc-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRevoke}
                  className="px-3 py-1.5 rounded text-sm font-mono text-red-400 border border-red-900 hover:bg-red-950/60 transition-colors"
                >
                  Revoke
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}
